/**
 * Utility functions for safely converting applicant name fields (which might be objects,
 * encrypted blocks, "[object Object]" strings, or undefined) into clean human-readable strings.
 *
 * WHY THIS MODULE EXISTS (PR-16): person-name fields do NOT have one stored
 * shape. Application PII (personalDetails.firstName / lastName / …) is stored
 * ENCRYPTED at rest as `{ iv, ciphertext, authTag }` blocks (see
 * src/lib/piiFieldMap.ts + secureApplicationsRepo), and any reader that did
 * not decrypt — most importantly the admin Workflow engine, which is
 * deliberately handed the RAW Firestore handle (server.ts registers the
 * workflow routes with `adminDb.__raw__`) — sees those blocks as objects.
 * Every `[parts].filter(Boolean).join(' ')` or `${firstName}` interpolation
 * over such a value renders the literal text "[object Object]". These
 * helpers are the ONE sanctioned way to turn a name-shaped value into a
 * display string: strings pass through, nested name objects are unwrapped,
 * encrypted blocks are treated as "name unavailable" (never stringified),
 * and already-poisoned "[object Object]" strings are rejected so callers
 * fall through to their next source (displayName → email → placeholder).
 */

/**
 * True when the value is an encrypted PII block ({ iv, ciphertext, authTag })
 * as written by the client secure repo / server PII helpers. Such a value
 * carries NO readable name — readers without the key must treat it as absent.
 */
export function isEncryptedPiiBlockShape(val: any): boolean {
  if (!val || typeof val !== 'object' || Array.isArray(val)) return false;
  const hasIv = typeof val.iv === 'string' && val.iv.length > 0;
  const hasCiphertext = typeof val.ciphertext === 'string' || typeof val.cipherText === 'string';
  const hasAuthTag = typeof val.authTag === 'string' && val.authTag.length > 0;
  return hasIv && hasCiphertext && hasAuthTag;
}

export function sanitizeNamePart(val: any): string {
  if (val === null || val === undefined) return '';

  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed || trimmed === '[object Object]' || trimmed.toLowerCase().includes('[object object]')) {
      return '';
    }
    return trimmed;
  }

  if (Array.isArray(val)) return '';

  if (typeof val === 'object') {
    // Encrypted PII block: the plaintext is not recoverable here. Return ''
    // so callers fall back to another source instead of printing the block.
    if (isEncryptedPiiBlockShape(val)) return '';
    // If object has properties like text, value, firstName, lastName, or name
    if (typeof val.firstName === 'string' || typeof val.lastName === 'string') {
      const f = sanitizeNamePart(val.firstName);
      const l = sanitizeNamePart(val.lastName);
      return [f, l].filter(Boolean).join(' ');
    }
    if (typeof val.first === 'string' || typeof val.last === 'string') {
      const f = sanitizeNamePart(val.first);
      const l = sanitizeNamePart(val.last);
      return [f, l].filter(Boolean).join(' ');
    }
    if (typeof val.text === 'string') return sanitizeNamePart(val.text);
    if (typeof val.value === 'string') return sanitizeNamePart(val.value);
    if (typeof val.name === 'string') return sanitizeNamePart(val.name);
    if (typeof val.cipherText === 'string' || typeof val.ciphertext === 'string') return ''; // Encrypted block fallback
    return '';
  }

  if (typeof val === 'number' && Number.isFinite(val)) return String(val);

  return '';
}

/**
 * Join name parts (first / middle / last, in ANY stored shape) into one
 * display string. Non-string parts are sanitized individually, so encrypted
 * blocks and other objects contribute nothing instead of "[object Object]".
 */
export function joinPersonName(parts: any[]): string {
  if (!Array.isArray(parts)) return '';
  return parts.map((p) => sanitizeNamePart(p)).filter(Boolean).join(' ');
}

/** First value, in order, that sanitizes to a non-empty name fragment. */
export function pickNamePart(...values: any[]): string {
  for (const v of values) {
    const s = sanitizeNamePart(v);
    if (s) return s;
  }
  return '';
}

/**
 * Resolve the best display name from a list of candidate records, in order.
 * For each record the sources are tried in this order:
 *   1. direct full-name fields (applicantName / userName / displayName / name)
 *   2. personalDetails name parts (firstName / middleName / lastName),
 *      then personalDetails.fullNameLegal
 *   3. top-level name parts, then top-level fullNameLegal
 * Deliberately NO email fallback — callers decide whether an email (or a
 * placeholder) is the right last resort for their surface.
 */
export function firstUsablePersonName(candidates: any[], fallback: string = ''): string {
  for (const c of candidates || []) {
    if (c === null || c === undefined) continue;
    if (typeof c !== 'object') {
      const s = sanitizeNamePart(c);
      if (s) return s;
      continue;
    }
    const direct = sanitizeNamePart(c.applicantName || c.userName || c.displayName || c.name);
    if (direct) return direct;

    const pd = c.personalDetails;
    if (pd && typeof pd === 'object') {
      const fromPd =
        joinPersonName([pd.firstName, pd.middleName, pd.lastName]) ||
        sanitizeNamePart(pd.fullNameLegal) ||
        sanitizeNamePart(pd.displayName);
      if (fromPd) return fromPd;
    }

    const fromTop =
      joinPersonName([c.firstName, c.middleName, c.lastName]) ||
      sanitizeNamePart(c.fullNameLegal);
    if (fromTop) return fromTop;
  }
  return fallback;
}

/**
 * Cleanly extracts full applicant name from an application or personalDetails object.
 */
export function getSafeApplicantName(appOrPersonalDetails: any, fallback: string = 'Applicant'): string {
  if (!appOrPersonalDetails) return fallback;

  const name = firstUsablePersonName([appOrPersonalDetails], '');
  if (name) return name;

  // Fallback to email prefix or fallback argument
  const pd = appOrPersonalDetails.personalDetails || appOrPersonalDetails;
  const email = sanitizeNamePart(appOrPersonalDetails.userEmail || appOrPersonalDetails.email || pd?.email);
  if (email) return email;

  return fallback;
}
